import type {
  ConversationListResponse,
  ConversationMetadataSchema,
} from "@harmonyso/conversations-viewer-sdk";
import type { Cell } from "./cells";
import type { CellFailure } from "./cell-tenants";

/**
 * How many conversations an "All cells" list can page through. The list is
 * sorted and paged by each cell's server, so page `p` of the merged list
 * needs each cell's first `p * pageSize` rows, and a cell returns at most
 * 200 rows per request.
 */
export const ALL_CELLS_LIST_LIMIT = 200;

/**
 * The page to ask each cell for. One cell is paged as usual; with several,
 * each returns its first rows, enough to cut the requested merged page.
 */
export function cellListPage(
  cellCount: number,
  page: number,
  pageSize: number,
): { page: number; page_size: number } {
  if (cellCount <= 1) {
    return { page, page_size: pageSize };
  }
  return {
    page: 1,
    page_size: Math.min(page * pageSize, ALL_CELLS_LIST_LIMIT),
  };
}

type SortValue = string | number | null;

const toSortValue = (value: unknown): SortValue => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" || typeof value === "string") return value;
  return null;
};

/** The columns the conversations list can be sorted by. */
const SORT_ACCESSORS: Record<
  string,
  ((conversation: ConversationMetadataSchema) => unknown) | undefined
> = {
  conversation_date: (c) => c.conversation_date,
  last_turn_at: (c) => c.last_turn_at,
  tenant_id: (c) => c.tenant_id,
  category: (c) => c.category,
  turn_count: (c) => c.turn_count,
  source: (c) => c.source,
};

const sortValue = (
  conversation: ConversationMetadataSchema,
  sortBy: string,
): SortValue => {
  const accessor = new Map(Object.entries(SORT_ACCESSORS)).get(sortBy);
  return accessor ? toSortValue(accessor(conversation)) : null;
};

/** Compares like the server's ORDER BY: nulls sort last ascending, first descending. */
export function compareConversations(
  sortBy: string,
  sortDirection: "asc" | "desc",
): (a: ConversationMetadataSchema, b: ConversationMetadataSchema) => number {
  const direction = sortDirection === "desc" ? -1 : 1;
  return (a, b) => {
    const left = sortValue(a, sortBy);
    const right = sortValue(b, sortBy);
    if (left === right) return 0;
    if (left === null) return direction;
    if (right === null) return -direction;
    if (typeof left === "number" && typeof right === "number") {
      return (left - right) * direction;
    }
    return String(left).localeCompare(String(right)) * direction;
  };
}

export interface CellConversationsResult {
  cell: Cell;
  data?: ConversationListResponse;
  error?: Error | null;
  isLoading?: boolean;
}

export interface MergedCellConversations {
  conversations: ConversationMetadataSchema[];
  /** Every loaded cell's matching conversations. */
  totalCount: number;
  /** The pages the table can show; an "All cells" list stops at the limit. */
  pageCount: number;
  /** True when "All cells" has more conversations than it can page through. */
  capped: boolean;
  perCell: { cell: Cell; total: number }[];
  failures: CellFailure[];
  loadingCells: Cell[];
}

/**
 * One page of conversations from every cell that answered. A single cell's
 * page is passed through; several cells' first rows are merged in the
 * server's sort order and the requested page is cut from them.
 */
export function mergeCellConversations(
  results: CellConversationsResult[],
  {
    page,
    pageSize,
    sortBy,
    sortDirection,
  }: {
    page: number;
    pageSize: number;
    sortBy: string;
    sortDirection: "asc" | "desc";
  },
): MergedCellConversations {
  const failures: CellFailure[] = [];
  const loadingCells: Cell[] = [];
  const loaded: { cell: Cell; data: ConversationListResponse }[] = [];

  for (const { cell, data, error, isLoading } of results) {
    if (error) {
      failures.push({ cell, error });
    } else if (data) {
      loaded.push({ cell, data });
    } else if (isLoading) {
      loadingCells.push(cell);
    }
  }

  const perCell = loaded.map(({ cell, data }) => ({
    cell,
    total: data.total_count,
  }));
  const totalCount = perCell.reduce((sum, { total }) => sum + total, 0);

  if (results.length <= 1) {
    const data = loaded.at(0)?.data;
    return {
      conversations: data?.conversations ?? [],
      totalCount,
      pageCount: data?.total_pages ?? 0,
      capped: false,
      perCell,
      failures,
      loadingCells,
    };
  }

  const start = (page - 1) * pageSize;
  // Rows past the limit aren't in the merged order: a cell returns at most that many.
  const conversations = loaded
    .flatMap(({ data }) => data.conversations)
    .sort(compareConversations(sortBy, sortDirection))
    .slice(0, ALL_CELLS_LIST_LIMIT)
    .slice(start, start + pageSize);
  const reachable = Math.min(totalCount, ALL_CELLS_LIST_LIMIT);

  return {
    conversations,
    totalCount,
    pageCount: Math.ceil(reachable / pageSize),
    capped: totalCount > ALL_CELLS_LIST_LIMIT,
    perCell,
    failures,
    loadingCells,
  };
}
